import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthProvider";
import { apiFetch } from "../services/api";
import { notifyGlobalNotice } from "../services/session";

/**
 * Assessment selection (frontend-only state)
 *
 * Why:
 * - Most workspace screens (Overview/Objects/Risks) must operate within ONE selected assessment.
 * - We keep the selected assessment in localStorage so a reload keeps context.
 * - We do NOT change any backend behavior/endpoints for this.
 */

export type AssessmentStats = {
  zones: number;
  conduits: number;
  components: number;
  risks: number;
  mitigations: number;
  open_issues: number;
  unmitigated_high: number;
  risk_low: number;
  risk_medium: number;
  risk_high: number;
  risk_critical: number;
};

export type AssessmentMetadata = {
  modified_at: string | null;
  modified_by: string | null;
};

export type AssessmentLatestReport = {
  id: number;
  assessment_id: number;
  filename: string;
  generated_at: string;
  size_bytes: number;
  content_type: string;
};

export type Assessment = {
  id: number;
  name: string;
  description?: string | null;
  scale_pair_version_id: number;
  visibility?: "public" | "private";
  architecture_frozen?: boolean;
  threat_modeling_enabled?: boolean;
  product_object_id?: number | null;
  created_at: string;
  metadata: AssessmentMetadata;
  stats: AssessmentStats;
  latest_report?: AssessmentLatestReport | null;
};

type Ctx = {
  assessments: Assessment[];
  loading: boolean;
  selectedId: number | null;
  selected: Assessment | null;
  setSelectedId: (id: number | null) => void;
  refresh: () => Promise<void>;
};

const KEY = "iec62443_selected_assessment_id";
const AssessmentCtx = createContext<Ctx | null>(null);

export function AssessmentProvider({ children }: { children: React.ReactNode }) {
  const { tokens } = useAuth();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const accessTokenRef = useRef<string | null>(tokens?.access_token ?? null);
  const hasLoadedForSessionRef = useRef(false);

  const [selectedId, setSelectedIdState] = useState<number | null>(() => {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  });

  const selected = useMemo(
    () => (selectedId ? assessments.find((a) => a.id === selectedId) ?? null : null),
    [assessments, selectedId]
  );

  const setSelectedId = (id: number | null) => {
    setSelectedIdState(id);
    if (id == null) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, String(id));
  };

  const refresh = useCallback(async () => {
    const accessToken = accessTokenRef.current;
    if (!accessToken) return;
    setLoading(true);
    try {
      const list = await apiFetch<Assessment[]>("/api/assessments", { method: "GET" }, accessToken);
      setAssessments(list);
      const hasSelected = selectedId ? list.some((a) => a.id === selectedId) : false;
      // If the previously selected ID no longer exists, clear it.
      if (selectedId && !hasSelected) {
        setSelectedId(null);
      }
    } catch (err: any) {
      const message = err?.message ?? "Failed to refresh assessments";
      notifyGlobalNotice("Assessment refresh failed. Please retry.");
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    accessTokenRef.current = tokens?.access_token ?? null;
  }, [tokens?.access_token]);

  useEffect(() => {
    const hasToken = Boolean(tokens?.access_token);
    if (!hasToken) {
      hasLoadedForSessionRef.current = false;
      setAssessments([]);
      setLoading(false);
      return;
    }
    if (hasLoadedForSessionRef.current) {
      return;
    }
    hasLoadedForSessionRef.current = true;
    void refresh();
  }, [tokens?.access_token, refresh]);

  const value = useMemo<Ctx>(
    () => ({ assessments, loading, selectedId, selected, setSelectedId, refresh }),
    [assessments, loading, selectedId, selected]
  );

  return <AssessmentCtx.Provider value={value}>{children}</AssessmentCtx.Provider>;
}

export function useAssessment() {
  const ctx = useContext(AssessmentCtx);
  if (!ctx) throw new Error("useAssessment must be used within AssessmentProvider");
  return ctx;
}
